import type { IAudioContext, IAudioNode } from 'standardized-audio-context'

import { AudioContext } from 'standardized-audio-context'
import { useEventListener } from '@vueuse/core'
import { ref } from 'vue'

// 1. Toggle for Web Audio (Turn this on only for Milkdrop)
export const isWebAudioRequested = ref(false)

// Audio nodes
export const AUDIO_CONTEXT = new AudioContext()
export const GAIN_NODE = AUDIO_CONTEXT.createGain()

// Track the gain value globally so Native and Web Audio can share it
export const currentGain = ref(1)

// Unlock AudioContext automatically
const UNLOCK_EVENTS = ['touchstart', 'touchend', 'mousedown', 'keydown']
for (const event of UNLOCK_EVENTS) {
  const stop = useEventListener(window, event, () => {
    if (AUDIO_CONTEXT.state === 'suspended') {
      AUDIO_CONTEXT.resume()
    }
    stop()
  }, { passive: true })
}

// Connect Gain Node
GAIN_NODE.connect(AUDIO_CONTEXT.destination)

export const setGain = (value: number) => {
  const safeValue = Math.max(0, Math.min(value, 1))
  currentGain.value = safeValue
  GAIN_NODE.gain.value = safeValue
}

// TODO (wvffle): Create equalizer filters
const equalizerFilters = [
  GAIN_NODE
]

let lastNode: IAudioNode<IAudioContext> | null = null

export const connectAudioSource = (sourceNode: IAudioNode<IAudioContext>) => {
  for (const filter of equalizerFilters) {
    try {
      lastNode?.disconnect(filter)
    } catch (e) {
      // NOTE: The error is probably InvalidAccessError, because the nodes aren't connected. We just ignore it.
    }

    sourceNode.connect(filter)
  }

  lastNode = sourceNode
}

// ── Stub node for iOS native mode ────────────────────────────────────────────
// Satisfies the IAudioNode interface without touching the AudioContext pipeline.
// The <audio> element outputs directly to system audio and survives backgrounding.
export class PassthroughNode {
  context = AUDIO_CONTEXT
  connect() { return this }
  disconnect() {}
  // Minimal stubs for the rest of the interface
  addEventListener() {}
  removeEventListener() {}
  dispatchEvent() { return true }
  numberOfInputs = 0
  numberOfOutputs = 1
  channelCount = 2
  channelCountMode = 'max' as const
  channelInterpretation = 'speakers' as const
}

export const createAudioSource = (sourceElement: HTMLAudioElement): IAudioNode<IAudioContext> => {
  if (!isWebAudioRequested.value) {
    return new PassthroughNode() as unknown as IAudioNode<IAudioContext>
  }

  return AUDIO_CONTEXT.createMediaElementSource(sourceElement)
}
